<template>
  <div v-show="hasRule" class="wd-flex wd-flex-col wd-gap-2 wd-w-full" :data-testid="testIDs.penalty.penaltyHintBlock">
    <div v-if="cancelTimeRuleLabel" class="wd-flex wd-gap-2 wd-w-full wd-text-sm sm:wd-text-base">
      <div class="wd-w-5 wd-h-5 sm:wd-w-6 sm:wd-h-6 wd-flex-shrink-0">
        <FunnowSvgIcon image-path="check.svg" max-width="24" class="wd-text-orange50" />
      </div>
      <div :data-testid="testIDs.penalty.cancelTimeRuleLabel">{{ cancelTimeRuleLabel }}</div>
    </div>
    <div v-if="cancelDepositLabel" class="wd-flex wd-gap-2 wd-w-full">
      <div class="wd-w-5 wd-h-5 sm:wd-w-6 sm:wd-h-6 wd-flex-shrink-0">
        <FunnowSvgIcon image-path="money-icon-rwd.svg" max-width="24" class="wd-text-orange50" />
      </div>
      <div class="wd-flex wd-flex-col wd-gap-1">
        <span class="wd-text-sm sm:wd-text-base" :data-testid="testIDs.penalty.cancelDepositLabel">{{ cancelDepositLabel }}</span>
        <span v-if="showSwappedCurrency" class="wd-text-xs sm:wd-text-sm wd-text-gray80" :data-testid="testIDs.penalty.cancelSwappedDepositLabel">
          {{ cancelSwappedDepositLabel }}
        </span>
      </div>
    </div>
    <div v-if="showCreditCardHint" class="wd-flex wd-gap-2 wd-text-sm sm:wd-text-base">
      <div class="wd-w-5 wd-h-5 sm:wd-w-6 sm:wd-h-6 wd-flex-shrink-0">
        <FunnowSvgIcon image-path="new-card-current-color.svg" max-width="24" class="wd-text-orange50" />
      </div>
      <div :data-testid="testIDs.penalty.creditCardBindingLabel">{{ creditCardDepositLabel }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isEmpty as _isEmpty } from 'lodash-es'
import FunnowSvgIcon from '@/components/icons/funnow-svg-icon.vue'
import { type ProductPenaltyRuleResult } from '@/view-models/product'
import { PaymentMethodsType } from '@/view-models/payment'
import testIDs from '@/constants/test-ids.json'

type Props = {
  penaltyRule: ProductPenaltyRuleResult | null
  showCreditCardHint?: boolean
  showCancelDate?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  penaltyRule: null,
  showCreditCardHint: false,
  showCancelDate: false,
})

const { penaltyRule, showCreditCardHint, showCancelDate } = toRefs(props)
const nuxtApp = useNuxtApp()

const {
  cancelTimeLabel,
  cancelTimeLabelForBooking,
  cancelDepositLabel,
  cancelSwappedDepositLabel,
  creditCardDepositLabel,
  hasRule: hasPenaltyRule,
} = useProductPenaltyRule(penaltyRule)

const cancelTimeRuleLabel = computed(() => (showCancelDate.value ? cancelTimeLabelForBooking.value : cancelTimeLabel.value))

const hasRule = computed(() => hasPenaltyRule.value || showCreditCardHint.value)

const paymentMethod = computed(() => nuxtApp.$store.getters['payment/paymentMethod'])
const showSwappedCurrency = computed(
  () =>
    paymentMethod.value?.type === PaymentMethodsType.SavedCreditCard &&
    !_isEmpty(nuxtApp.$store.getters['payment/selectedCreditCard']) &&
    nuxtApp.$store.getters['payment/selectedCreditCard'].currency?.id !== penaltyRule.value?.no_show.currency_id
)
</script>
